import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonIcon,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import {
  search as searchIcon,
  documentText,
  chevronBack,
  chevronForward,
  chevronDown,
  chevronUp,
  time,
  downloadOutline,
} from 'ionicons/icons';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useAuditLog, useAuditActions, useAdminUsers } from '@/hooks/useAdmin';
import { exportAuditLog } from '@/api/admin';
import { AUDIT_ACTION_LABELS, AUDIT_CATEGORY_LABELS, AUDIT_CATEGORY_COLORS } from '@/utils/labels';
import type { AuditCategory, AuditLogFilters } from '@/types/admin';

/* ------------------------------------------------------------------
   Action color map
   ------------------------------------------------------------------ */
const ACTION_COLORS: Record<string, string> = {
  created: '#059669',
  updated: '#4f46e5',
  deleted: '#dc2626',
  restored: '#0d9488',
  login: '#0891b2',
  login_failed: '#f97316',
  login_blocked: '#dc2626',
  logout: '#64748b',
  password_reset: '#7c3aed',
  password_reset_requested: '#a78bfa',
  password_reset_completed: '#7c3aed',
  password_reset_failed: '#ef4444',
  status_changed: '#7c3aed',
  user_activated: '#059669',
  user_deactivated: '#dc2626',
  roles_updated: '#4f46e5',
  viewed: '#6366f1',
  bulk_updated: '#d97706',
  document_generated: '#d97706',
  member_added: '#059669',
  member_removed: '#dc2626',
  member_role_updated: '#4f46e5',
  members_batch_added: '#059669',
  export: '#d97706',
};

/* ------------------------------------------------------------------
   Sliding pagination window
   ------------------------------------------------------------------ */
function getPageWindow(current: number, total: number, size = 5): number[] {
  const half = Math.floor(size / 2);
  let start = Math.max(1, current - half);
  const end = Math.min(total, start + size - 1);
  start = Math.max(1, end - size + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/* ------------------------------------------------------------------
   Component
   ------------------------------------------------------------------ */
const AuditLogPage: React.FC = () => {
  const [actionFilter, setActionFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<AuditCategory | ''>('');
  const [userFilter, setUserFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const pageSize = 25;
  const searchTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounce search input
  useEffect(() => {
    searchTimer.current = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(searchTimer.current);
  }, [searchText]);

  const filters: AuditLogFilters = {
    action: actionFilter || undefined,
    category: categoryFilter || undefined,
    user_id: userFilter || undefined,
    search: debouncedSearch || undefined,
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
    page: currentPage,
    page_size: pageSize,
  };

  const { data, isLoading, refetch } = useAuditLog(filters);
  const { data: actionsList } = useAuditActions();
  const { data: usersData } = useAdminUsers({ page_size: 200 });

  const entries = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const users = usersData?.data ?? [];

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const resetFilters = () => {
    setActionFilter('');
    setCategoryFilter('');
    setUserFilter('');
    setSearchText('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const handleExport = useCallback(async () => {
    try {
      const blob = await exportAuditLog(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silent fail
    }
  }, [filters]);

  const renderDiff = (oldVals: Record<string, unknown> | null, newVals: Record<string, unknown> | null) => {
    if (!oldVals && !newVals) return <p style={{ fontSize: 12, color: '#94a3b8' }}>Fara detalii</p>;

    const allKeys = new Set([
      ...Object.keys(oldVals ?? {}),
      ...Object.keys(newVals ?? {}),
    ]);

    return (
      <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
        {[...allKeys].map((key) => {
          const oldVal = oldVals?.[key];
          const newVal = newVals?.[key];
          const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);

          if (!changed) return null;

          return (
            <div key={key} style={{ padding: '2px 0' }}>
              <span style={{ fontWeight: 600, color: '#475569' }}>{key}: </span>
              {oldVal !== undefined && (
                <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '1px 4px', borderRadius: 3, textDecoration: 'line-through' }}>
                  {String(oldVal ?? 'null')}
                </span>
              )}
              {oldVal !== undefined && newVal !== undefined && <span style={{ margin: '0 4px', color: '#94a3b8' }}>&rarr;</span>}
              {newVal !== undefined && (
                <span style={{ backgroundColor: '#ecfdf5', color: '#059669', padding: '1px 4px', borderRadius: 3 }}>
                  {String(newVal ?? 'null')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const hasActiveFilters = actionFilter || categoryFilter || userFilter || debouncedSearch || fromDate || toDate;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Jurnal Audit</IonTitle>
          <IonButtons slot="end">
            <button className="crm-toolbar-icon" onClick={handleExport} title="Export CSV">
              <IonIcon icon={downloadOutline} />
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="crm-page-layout">
          {/* === LEFT STATS PANEL === */}
          <div className="crm-stats-panel">
            <div>
              <div className="crm-section-header">
                <h3 className="crm-section-title">Jurnal Activitate</h3>
                <p className="crm-section-subtitle">Cine, ce, cand</p>
              </div>
            </div>

            <div className="crm-stat-card crm-animate-in">
              <div className="crm-stat-card__label">
                <span>Total Inregistrari</span>
                <div className="crm-stat-card__icon" style={{ backgroundColor: '#4f46e520', color: '#4f46e5' }}>
                  <IonIcon icon={documentText} />
                </div>
              </div>
              <div className="crm-stat-card__value">{totalCount}</div>
            </div>

            {/* Category filter */}
            <div className="crm-animate-in">
              <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', margin: '0 0 8px' }}>
                Categorie
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                {Object.entries(AUDIT_CATEGORY_LABELS).map(([key, label]) => {
                  const isActive = categoryFilter === key;
                  const color = AUDIT_CATEGORY_COLORS[key] ?? '#64748b';
                  return (
                    <div
                      key={key}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 12,
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: isActive ? 600 : 400,
                        backgroundColor: isActive ? `${color}20` : '#f1f5f9',
                        color: isActive ? color : '#64748b',
                        border: `1px solid ${isActive ? color : 'transparent'}`,
                      }}
                      onClick={() => { setCategoryFilter(isActive ? '' : key as AuditCategory); setCurrentPage(1); }}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action filter */}
            <div className="crm-animate-in">
              <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', margin: '0 0 8px' }}>
                Filtrare Actiune
              </h4>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {(actionsList ?? Object.keys(AUDIT_ACTION_LABELS)).map((key) => {
                  const isActive = actionFilter === key;
                  const color = ACTION_COLORS[key] ?? '#64748b';
                  return (
                    <div
                      key={key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '5px 10px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        backgroundColor: isActive ? `${color}10` : 'transparent',
                        marginBottom: 1,
                      }}
                      onClick={() => { setActionFilter(isActive ? '' : key); setCurrentPage(1); }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? color : '#475569' }}>
                        {AUDIT_ACTION_LABELS[key] ?? key}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* User filter */}
            <div className="crm-animate-in" style={{ marginTop: 12 }}>
              <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', margin: '0 0 8px' }}>
                Utilizator
              </h4>
              <select
                value={userFilter}
                onChange={(e) => { setUserFilter(e.target.value); setCurrentPage(1); }}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  fontSize: 12,
                  color: '#475569',
                  backgroundColor: '#fff',
                }}
              >
                <option value="">Toti utilizatorii</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                ))}
              </select>
            </div>

            {/* Date range filter */}
            <div className="crm-animate-in" style={{ marginTop: 12 }}>
              <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', margin: '0 0 8px' }}>
                Perioada
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                  placeholder="De la"
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                    color: '#475569',
                  }}
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                  placeholder="Pana la"
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                    color: '#475569',
                  }}
                />
              </div>
            </div>

            {/* Reset filters */}
            {hasActiveFilters && (
              <div className="crm-animate-in" style={{ marginTop: 12 }}>
                <button
                  onClick={resetFilters}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#f8fafc',
                    color: '#64748b',
                    fontSize: 12,
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  Reseteaza filtrele
                </button>
              </div>
            )}
          </div>

          {/* === MAIN CONTENT === */}
          <div className="crm-main-content">
            {/* Search bar */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ position: 'relative' }}>
                <IonIcon
                  icon={searchIcon}
                  style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 16 }}
                />
                <input
                  type="text"
                  placeholder="Cauta in descrieri..."
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 36px',
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    fontSize: 13,
                    color: '#334155',
                    backgroundColor: '#fff',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div className="crm-card" style={{ overflow: 'auto' }}>
              {isLoading ? (
                <div style={{ padding: 20 }}>
                  {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <div key={i} style={{ padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <IonSkeletonText animated style={{ width: '70%', height: 16 }} />
                      <IonSkeletonText animated style={{ width: '40%', height: 12, marginTop: 6 }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  {entries.map((entry) => {
                    const color = ACTION_COLORS[entry.action] ?? '#64748b';
                    const isExpanded = expandedId === entry.id;
                    const hasDiff = entry.old_values || entry.new_values;

                    return (
                      <div
                        key={entry.id}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          padding: '14px 20px',
                          cursor: hasDiff ? 'pointer' : 'default',
                        }}
                        onClick={() => hasDiff && toggleExpand(entry.id)}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          {/* Timeline dot */}
                          <div style={{ marginTop: 4, flexShrink: 0 }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }} />
                          </div>

                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span className="crm-badge" style={{ backgroundColor: `${color}15`, color }}>
                                {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                              </span>
                              {entry.category && entry.category !== 'data' && (
                                <span
                                  className="crm-badge"
                                  style={{
                                    backgroundColor: `${AUDIT_CATEGORY_COLORS[entry.category] ?? '#64748b'}15`,
                                    color: AUDIT_CATEGORY_COLORS[entry.category] ?? '#64748b',
                                    fontSize: 10,
                                  }}
                                >
                                  {AUDIT_CATEGORY_LABELS[entry.category] ?? entry.category}
                                </span>
                              )}
                              {entry.model_type && (
                                <span style={{ fontSize: 12, color: '#64748b' }}>
                                  {entry.model_type}
                                </span>
                              )}
                            </div>

                            {/* Description (primary text when available) */}
                            {entry.description && (
                              <div style={{ fontSize: 13, color: '#334155', marginTop: 4, fontWeight: 500 }}>
                                {entry.description}
                              </div>
                            )}

                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                                {entry.user_name ?? 'Sistem'}
                              </span>
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                <IonIcon icon={time} style={{ fontSize: 10, verticalAlign: 'middle', marginRight: 3 }} />
                                {format(parseISO(entry.created_at), 'd MMM yyyy, HH:mm', { locale: ro })}
                              </span>
                              {entry.ip_address && (
                                <span style={{ fontSize: 10, color: '#cbd5e1' }}>
                                  IP: {entry.ip_address}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Expand icon */}
                          {hasDiff && (
                            <IonIcon
                              icon={isExpanded ? chevronUp : chevronDown}
                              style={{ fontSize: 16, color: '#94a3b8', flexShrink: 0, marginTop: 4 }}
                            />
                          )}
                        </div>

                        {/* Expanded diff */}
                        {isExpanded && hasDiff && (
                          <div style={{ marginTop: 12, marginLeft: 22, padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                              Modificari
                            </div>
                            {renderDiff(entry.old_values, entry.new_values)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!isLoading && entries.length === 0 && (
                <div className="crm-empty-state">
                  <div className="crm-empty-state__icon"><IonIcon icon={documentText} /></div>
                  <h3 className="crm-empty-state__title">Nicio inregistrare</h3>
                  <p className="crm-empty-state__text">
                    {hasActiveFilters ? 'Nu exista inregistrari pentru aceste filtre.' : 'Jurnalul de audit este gol.'}
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="crm-pagination">
                  <button className="crm-pagination__btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
                  </button>
                  {getPageWindow(currentPage, totalPages).map((p) => (
                    <button
                      key={p}
                      className={`crm-pagination__num ${p === currentPage ? 'crm-pagination__num--active' : ''}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  <button className="crm-pagination__btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AuditLogPage;
